import { type UnitInsightChatResponse } from '../../../../api/customEndpoints/getUnitInsightLlmChat';
import { ChatMessageHistory } from './ChatMessageHistory';
import { type UnitPerformanceLlmInsightResponse } from '../../../../api/customEndpoints/fetchUnitPerformanceScores';
import { ChatMessageForm } from './ChatMessageForm';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslate } from 'ra-core';

export function ChatView({
  unitId,
  chatId,
  chat,
  insight,
  refetchChatHistory,
  newChatCreated,
}: {
  unitId: string | number;
  chatId?: number;
  chat?: UnitInsightChatResponse;
  insight: UnitPerformanceLlmInsightResponse;
  refetchChatHistory: () => Promise<void>;
  newChatCreated: (chatId: number) => void;
}) {
  const translate = useTranslate();
  const [pendingUserMessage, setPendingUserMessage] = useState<{
    text: string;
    fileUrls: string[];
  }>();
  const [streamingReply, setStreamingReply] = useState<string>();
  const streamingChatIdRef = useRef<number>(undefined);

  // Scroll to bottom on mount
  const bottomRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ block: 'start' });
    }
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4">
      <div className="min-h-0 flex-1">
        <ChatMessageHistory
          chat={chat}
          insight={insight}
          pendingUserMessage={pendingUserMessage}
          streamingAssistantMessage={
            streamingReply ||
            (pendingUserMessage
              ? translate('moonstar.unitPerformance.chat.thinking')
              : undefined)
          }
        />
      </div>
      <div>
        <ChatMessageForm
          unitId={unitId}
          insightId={insight.id}
          chatId={chatId}
          onMessageSent={(message) => {
            setStreamingReply('');
            setPendingUserMessage({
              text: message.text,
              fileUrls: [],
            });
          }}
          onReplyStarted={(chatId) => {
            streamingChatIdRef.current = chatId;
          }}
          onReplyChunk={(chunk) => {
            setStreamingReply((prev) => (prev ? `${prev}${chunk}` : chunk));
          }}
          onReplyComplete={() => {
            if (chatId !== streamingChatIdRef.current) {
              newChatCreated(streamingChatIdRef.current!);
            } else {
              void refetchChatHistory().finally(() => {
                setPendingUserMessage(undefined);
                setStreamingReply(undefined);
              });
            }
          }}
          onReplyError={() => {
            setStreamingReply(
              translate('moonstar.unitPerformance.chat.replyFailed')
            );
          }}
        />
        <div ref={bottomRef}></div>
      </div>
    </div>
  );
}
